import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import useStoreAppConfig from '@hooks/useStoreAppConfig';
import useStoreAuth from '@hooks/useStoreAuth';

import { TourStep } from './OnboardingTour.js';
import {
  getTourFloorHolder,
  markTourStarted,
  releaseTourFloor,
  requestTourFloor,
  subscribeTourFloor,
} from './tourQueue.js';
import { DEFAULT_TOUR_PRIORITY } from './tourRegistry.js';

export type UseOnboardingTourOptions = {
  // Gate the automatic first-visit open until the target elements exist in the DOM
  // (e.g. after the page data has loaded). Defaults to true.
  ready?: boolean;
  // Order among tours eligible at the same time; lower opens first. See `TOUR_PRIORITY`.
  priority?: number;
};

export type UseOnboardingTourResult = {
  open: boolean;
  steps: TourStep[];
  onClose: () => void;
  // Closes without consuming the once-per-user flag, for a tour that could not be shown at
  // all (no step target on screen). It opens again on a viewport where the target exists.
  onUnavailable: () => void;
};

/**
 * Drives a first-visit onboarding tour: auto-opens once per user until dismissed and
 * persists the "seen" state per user. The `tourId` scopes the seen flag, so each
 * feature gets its own independent tour. Concurrently eligible tours are serialized
 * through the shared tour queue, so only one spotlight is ever on screen.
 */
export function useOnboardingTour(
  tourId: string,
  steps: TourStep[],
  options?: UseOnboardingTourOptions
): UseOnboardingTourResult {
  const ready = options?.ready ?? true;
  const priority = options?.priority ?? DEFAULT_TOUR_PRIORITY;

  const [{ userId }] = useStoreAuth();
  const [{ seenTours }, { markTourSeen }] = useStoreAppConfig();

  const [open, setOpen] = useState(false);
  const hasAutoOpened = useRef(false);

  const seen = !!userId && !!seenTours?.[userId]?.includes(tourId);
  const isEligible = ready && !!userId && !seen;

  const floorHolder = useSyncExternalStore(subscribeTourFloor, getTourFloorHolder);

  // Queue up while eligible, and drop out of the queue if this tour stops being eligible or
  // its host unmounts, so a tour waiting behind it is not blocked forever.
  useEffect(() => {
    if (!isEligible || hasAutoOpened.current) return undefined;
    requestTourFloor(tourId, priority);
    return () => releaseTourFloor(tourId);
  }, [isEligible, priority, tourId]);

  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (!isEligible || floorHolder !== tourId) return;
    hasAutoOpened.current = true;
    markTourStarted(tourId);
    setOpen(true);
  }, [floorHolder, isEligible, tourId]);

  const closeTour = useCallback(
    (markSeen: boolean) => {
      setOpen(false);
      releaseTourFloor(tourId);
      if (markSeen && userId) markTourSeen({ userId, tourId });
    },
    [markTourSeen, tourId, userId]
  );

  // Eligibility can be withdrawn while the tour is on screen — the viewport drops below `md` and
  // the sidebar becomes a closed drawer, or the rollout flag stops applying after a chain switch.
  // Close it here rather than letting the queue effect's cleanup hand the floor to the next tour
  // while this one stays open: that put two spotlights on screen at once, the older one anchored
  // to an element that is no longer rendered. The flag is not consumed and the auto-open latch is
  // released, so the tour runs again once it is eligible — it cannot loop, because reopening
  // requires eligibility to come back, not merely a missing target.
  useEffect(() => {
    if (isEligible || !open) return;
    hasAutoOpened.current = false;
    closeTour(false);
  }, [closeTour, isEligible, open]);

  const onClose = useCallback(() => closeTour(true), [closeTour]);
  const onUnavailable = useCallback(() => closeTour(false), [closeTour]);

  return useMemo(
    () => ({ open, steps, onClose, onUnavailable }),
    [onClose, onUnavailable, open, steps]
  );
}

export default useOnboardingTour;
